import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Brain, Zap, Calculator, Trophy, ClipboardCheck, Star, Award, Target, TrendingUp, Gamepad2, Sparkles } from 'lucide-react';
import FlashNumberGame from './FlashNumberGame';
import MathOperationsGame from './MathOperationsGame';
import MentalMemoryGame from './MentalMemoryGame';
import GameLeaderboard from './GameLeaderboard';
import StudentExamView from './StudentExamView';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

const MentalGames = ({ user }) => {
  const [selectedGame, setSelectedGame] = useState(null);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showExams, setShowExams] = useState(false);
  const [gameStats, setGameStats] = useState({ total_games: 0, best_score: 0, avg_score: 0 });
  const [recentScores, setRecentScores] = useState([]);

  // Load game stats
  useEffect(() => {
    if (user && user.role === 'student') {
      loadGameStats();
    }
  }, [user]);

  // Hide header when game is selected
  useEffect(() => {
    const header = document.querySelector('header');
    if (selectedGame) {
      if (header) {
        header.style.display = 'none';
      }
    } else {
      if (header) {
        header.style.display = '';
      }
    }

    // Cleanup: Show header when component unmounts
    return () => {
      if (header) {
        header.style.display = '';
      }
    };
  }, [selectedGame]);

  const loadGameStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/game-scores?student_id=${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const scores = response.data;
      
      if (scores.length > 0) {
        const total = scores.length;
        const best = Math.max(...scores.map(s => s.score || 0));
        const avg = Math.round(scores.reduce((sum, s) => sum + (s.score || 0), 0) / total);
        setGameStats({ total_games: total, best_score: best, avg_score: avg });
        setRecentScores(scores.slice(0, 5));
      }
    } catch (error) {
      console.error('Error loading game stats:', error);
    }
  };

  const games = [
    {
      id: 'flash_number',
      title: 'Flash Number Game',
      description: 'Remember numbers flashed on screen',
      icon: Zap,
      color: 'from-purple-500 to-pink-500',
      component: FlashNumberGame
    },
    {
      id: 'mental_memory',
      title: 'Mental Memory Game',
      description: 'Remember and type number sequences',
      icon: Brain,
      color: 'from-indigo-500 to-purple-600',
      component: MentalMemoryGame
    },
    {
      id: 'math_operations',
      title: 'Math Operations',
      description: 'Practice addition and multiplication',
      icon: Calculator,
      color: 'from-blue-500 to-cyan-500',
      component: MathOperationsGame
    }
  ];

  if (showExams) {
    return (
      <div>
        <Button 
          onClick={() => setShowExams(false)} 
          variant="outline" 
          className="mb-4"
        >
          ← Back to Games
        </Button>
        <StudentExamView user={user} />
      </div>
    );
  }

  if (showLeaderboard) {
    return (
      <div>
        <Button 
          onClick={() => setShowLeaderboard(false)} 
          variant="outline" 
          className="mb-4"
        >
          ← Back to Games
        </Button>
        <GameLeaderboard user={user} />
      </div>
    );
  }

  if (selectedGame) {
    const GameComponent = selectedGame.component;
    
    return (
      <div>
        <GameComponent user={user} onExit={() => setSelectedGame(null)} />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      {/* Header Section with Animated Background */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-purple-600 via-pink-600 to-indigo-600 p-8 text-white">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-4 bg-white/20 backdrop-blur-sm rounded-2xl animate-pulse">
                <Gamepad2 className="w-10 h-10 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-bold mb-2 flex items-center gap-2">
                  Mental Games Arena
                  <Sparkles className="w-8 h-8 animate-spin" style={{ animationDuration: '3s' }} />
                </h1>
                <p className="text-purple-100 text-lg">Challenge your mind and climb the leaderboard!</p>
              </div>
            </div>
            <div className="flex gap-3">
              {user.role === 'student' && (
                <Button 
                  onClick={() => setShowExams(true)}
                  className="flex items-center gap-2 bg-white/20 backdrop-blur-sm hover:bg-white/30 border-2 border-white/40"
                  size="lg"
                >
                  <ClipboardCheck className="w-5 h-5" />
                  My Exams
                </Button>
              )}
              <Button 
                onClick={() => setShowLeaderboard(true)}
                className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white border-none shadow-lg"
                size="lg"
              >
                <Trophy className="w-5 h-5" />
                Leaderboard
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards - Only for Students */}
      {user.role === 'student' && gameStats.total_games > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 hover:shadow-lg transition-all">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-600 font-semibold mb-1">Total Games</p>
                <p className="text-3xl font-bold text-blue-900">{gameStats.total_games}</p>
              </div>
              <div className="p-4 bg-blue-500 rounded-full">
                <Target className="w-8 h-8 text-white" />
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-green-50 to-green-100 border-green-200 hover:shadow-lg transition-all">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-600 font-semibold mb-1">Best Score</p>
                <p className="text-3xl font-bold text-green-900">{gameStats.best_score}</p>
              </div>
              <div className="p-4 bg-green-500 rounded-full">
                <Award className="w-8 h-8 text-white" />
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200 hover:shadow-lg transition-all">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-600 font-semibold mb-1">Average Score</p>
                <p className="text-3xl font-bold text-purple-900">{gameStats.avg_score}</p>
              </div>
              <div className="p-4 bg-purple-500 rounded-full">
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Game Cards with Enhanced Design */}
      <div>
        <h2 className="text-2xl font-bold mb-4 flex items-center gap-2 text-gray-800">
          <Star className="w-6 h-6 text-yellow-500" />
          Choose Your Challenge
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {games.map((game, index) => {
            const Icon = game.icon;
            return (
              <Card 
                key={game.id} 
                className="group relative hover:shadow-2xl transition-all duration-500 cursor-pointer overflow-hidden border-2 hover:border-purple-300 transform hover:-translate-y-2"
                onClick={() => setSelectedGame(game)}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Animated Background Gradient */}
                <div className={`absolute inset-0 bg-gradient-to-br ${game.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
                
                {/* Game Icon Section */}
                <div className={`relative h-40 bg-gradient-to-br ${game.color} flex flex-col items-center justify-center`}>
                  <Icon className="w-20 h-20 text-white group-hover:scale-125 group-hover:rotate-12 transition-all duration-500 drop-shadow-lg" />
                  <div className="mt-3 px-4 py-1 bg-white/30 backdrop-blur-sm rounded-full">
                    <p className="text-white text-xs font-semibold">Click to Play</p>
                  </div>
                </div>

                {/* Game Info Section */}
                <div className="relative p-6 bg-white group-hover:bg-gradient-to-br group-hover:from-white group-hover:to-purple-50 transition-all duration-300">
                  <h3 className="text-xl font-bold mb-2 text-gray-900 group-hover:text-purple-900">
                    {game.title}
                  </h3>
                  <p className="text-gray-600 mb-4 text-sm group-hover:text-gray-700">
                    {game.description}
                  </p>
                  
                  <Button className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold shadow-lg group-hover:shadow-xl transition-all">
                    <Zap className="w-4 h-4 mr-2" />
                    Start Game
                  </Button>
                </div>

                {/* Floating Badge */}
                <div className="absolute top-4 right-4 px-3 py-1 bg-yellow-400 text-yellow-900 rounded-full text-xs font-bold shadow-lg opacity-0 group-hover:opacity-100 transition-opacity">
                  🎮 Play Now
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Recent Scores - Only for Students */}
      {user.role === 'student' && recentScores.length > 0 && (
        <Card className="p-6 bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200">
          <h3 className="text-xl font-bold mb-4 flex items-center gap-2 text-indigo-900">
            <Star className="w-6 h-6 text-yellow-500" />
            Recent Achievements
          </h3>
          <div className="space-y-3">
            {recentScores.map((score, index) => (
              <div 
                key={score.id || index} 
                className="flex items-center justify-between p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                    index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : 'bg-orange-400'
                  }`}>
                    {index + 1}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{score.game_type?.replace('_', ' ') || 'Game'}</p>
                    <p className="text-xs text-gray-500">{new Date(score.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-indigo-600">{score.score}</p>
                  <p className="text-xs text-gray-500">points</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default MentalGames;
